/**
 * Modal for creating, editing draft, and acting on device transfers.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import StatusNotice from '../../../shared/components/common/StatusNotice';
import TransferStatusBadge from './TransferStatusBadge';
import TransferActor from './TransferActor';
import TransferRouteStack from './TransferRouteStack';
import TransferDeviceViewModal from './TransferDeviceViewModal';
import TransferViewDetails from './TransferViewDetails';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import {
  formatTransferDateTime,
} from '../transferDateUtils';
import './DeviceTransferModal.css';

const TransferModalFooter = ({ children, start, end }) => {
  if (end != null) {
    const hasStart = Boolean(start);
    return (
      <div className={`transfer-modal-footer${hasStart ? ' transfer-modal-footer--split' : ''}`}>
        {hasStart && <div className="transfer-modal-footer-start">{start}</div>}
        <div className="transfer-modal-footer-end">{end}</div>
      </div>
    );
  }
  return <div className="transfer-modal-footer">{children}</div>;
};

const PENDING_ACTION_LABELS = {
  'save-draft': 'Saving draft…',
  submit: 'Submitting for approval…',
  cancel: 'Cancelling transfer…',
  'approve-source': 'Approving at source…',
  'reject-source': 'Rejecting at source…',
  'accept-destination': 'Accepting at destination…',
  'reject-destination': 'Rejecting at destination…',
};

const TransferModalBusyOverlay = ({ label }) => (
  <div className="transfer-modal-busy-overlay" role="status" aria-live="polite" aria-busy="true">
    <div className="transfer-modal-busy-content">
      <div className="transfer-modal-spinner" aria-hidden="true" />
      <p className="transfer-modal-busy-label">{label}</p>
      <p className="transfer-modal-busy-hint">Please wait — this may take a few seconds.</p>
    </div>
  </div>
);

const LocationTransferModal = ({
  transfer,
  mode = 'view',
  direction = 'outgoing',
  token,
  user,
  device = null,
  priorRejections = [],
  onClose,
  onSuccess,
  onNavigateToLocationTransfers,
}) => {
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [toLocationId, setToLocationId] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [decisionReason, setDecisionReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [custodianId, setCustodianId] = useState('');
  const [custodians, setCustodians] = useState([]);
  const [custodiansLoading, setCustodiansLoading] = useState(false);
  const [departmentId, setDepartmentId] = useState('');
  const [destinationPhysicalLocation, setDestinationPhysicalLocation] = useState('');
  const [primaryTechId, setPrimaryTechId] = useState('');
  const [secondaryTechId, setSecondaryTechId] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [draftSavedInfo, setDraftSavedInfo] = useState(null);
  const [submittedInfo, setSubmittedInfo] = useState(null);
  const [viewTransfer, setViewTransfer] = useState(transfer);
  const [priorAcknowledged, setPriorAcknowledged] = useState(false);
  const [showDeviceView, setShowDeviceView] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const isCreate = mode === 'create';
  const isEditDraft = mode === 'edit-draft';
  const isView = mode === 'view';
  const isDraftForm = isCreate || isEditDraft;
  const hasRequestReason = Boolean(requestReason.trim());
  /** Read-only view may dismiss on backdrop click; any mode with inputs may not. */
  const closeOnBackdropClick = isView;
  const showPriorRejectionGate =
    isCreate && priorRejections.length > 0 && !priorAcknowledged;
  const isAdmin = user?.role === 'admin';
  const headers = { Authorization: `Bearer ${token}` };

  const deviceIdForLocations = device?.id || transfer?.device_id;
  const transferId =
    transfer?.transfer_id ||
    transfer?.id ||
    draftSavedInfo?.transfer_id ||
    draftSavedInfo?.id;
  const transferStatus =
    viewTransfer?.status || transfer?.status || draftSavedInfo?.status;
  const initiatorEmployeeId =
    viewTransfer?.requested_by_employee_id ||
    transfer?.requested_by_employee_id ||
    draftSavedInfo?.requested_by_employee_id;
  const isInitiator = Boolean(user?.employee_id && initiatorEmployeeId === user.employee_id);
  const canCancelAsInitiator =
    isInitiator &&
    transferId &&
    (transferStatus === 'draft' || transferStatus === 'pending_source_approval');
  const destinationLocationId =
    viewTransfer?.to_location_id || transfer?.to_location_id || null;
  const isAcceptDestination = mode === 'accept-destination' && isAdmin;

  useEffect(() => {
    setViewTransfer(transfer);
  }, [transfer]);

  /** Lock page scroll while transfer modal is open (matches ApprovalModal / ConfirmationModal). */
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow || 'unset';
      document.documentElement.style.overflow = previousHtmlOverflow || 'unset';
    };
  }, []);

  useEffect(() => {
    setPriorAcknowledged(priorRejections.length === 0);
  }, [priorRejections, mode]);

  useEffect(() => {
    if (isEditDraft && transfer) {
      setToLocationId(String(transfer.to_location_id || ''));
      setRequestReason(transfer.request_reason || '');
    }
  }, [isEditDraft, transfer]);

  useEffect(() => {
    if (!isView || !transferId) return;
    const loadTransfer = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.LOCATION_TRANSFERS.GET(transferId), { headers });
        if (res.data?.transfer) {
          setViewTransfer(res.data.transfer);
        }
      } catch (e) {
        console.error('Failed to load transfer details', e);
      }
    };
    loadTransfer();
  }, [isView, transferId, token]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isDraftForm && deviceIdForLocations) {
          const locRes = await axios.get(
            API_ENDPOINTS.LOCATION_TRANSFERS.DESTINATION_LOCATIONS(deviceIdForLocations),
            { headers }
          );
          setLocations(locRes.data?.locations || []);
          return;
        }

        if (!isAdmin) return;

        const deptRes = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, { headers });
        setDepartments(deptRes.data?.departments || deptRes.data || []);
      } catch (e) {
        console.error('Transfer modal load failed', e);
        if (isDraftForm) {
          setError(e.response?.data?.error || 'Failed to load destination locations');
        }
      }
    };
    load();
  }, [isDraftForm, isAdmin, token, deviceIdForLocations]);

  useEffect(() => {
    if (!isAcceptDestination || !departmentId) {
      setCustodians([]);
      if (!departmentId) {
        setCustodianId('');
      }
      return;
    }

    const loadCustodians = async () => {
      setCustodiansLoading(true);
      try {
        const res = await axios.get(
          API_ENDPOINTS.USERS.BY_ROLE('custodian', { department_id: departmentId }),
          { headers }
        );
        const list = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setCustodians(list);
        if (list.length > 0) {
          const defaultCustodianId = list[0].employee_id || '';
          setCustodianId(defaultCustodianId);
        } else {
          setCustodianId('');
        }
      } catch (e) {
        console.error('Failed to load destination custodians', e);
        setCustodians([]);
        setCustodianId('');
      } finally {
        setCustodiansLoading(false);
      }
    };

    loadCustodians();
  }, [isAcceptDestination, departmentId, token]);

  useEffect(() => {
    if (!isAcceptDestination || !departmentId || !destinationLocationId) {
      setTechnicians([]);
      if (!departmentId) {
        setPrimaryTechId('');
        setSecondaryTechId('');
      }
      return;
    }

    const loadTechnicians = async () => {
      setTechniciansLoading(true);
      try {
        const res = await axios.get(
          API_ENDPOINTS.USERS.TECHNICIANS({
            department_id: departmentId,
            location_id: destinationLocationId,
          }),
          { headers }
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setTechnicians(list);
        if (list.length > 0) {
          const defaultPrimaryId = String(list[0].id);
          setPrimaryTechId(defaultPrimaryId);
          setSecondaryTechId((prev) => {
            if (!prev || prev === defaultPrimaryId) return '';
            return list.some((t) => String(t.id) === prev) ? prev : '';
          });
        } else {
          setPrimaryTechId('');
          setSecondaryTechId('');
        }
      } catch (e) {
        console.error('Failed to load destination technicians', e);
        setTechnicians([]);
        setPrimaryTechId('');
        setSecondaryTechId('');
      } finally {
        setTechniciansLoading(false);
      }
    };

    loadTechnicians();
  }, [isAcceptDestination, departmentId, destinationLocationId, token]);

  const handleDestinationDepartmentChange = (e) => {
    setDepartmentId(e.target.value);
    setCustodianId('');
    setPrimaryTechId('');
    setSecondaryTechId('');
  };

  const beginPending = (actionKey) => {
    setSubmitting(true);
    setPendingAction(actionKey);
    setError('');
    setMessage('');
  };

  const clearPending = () => {
    setSubmitting(false);
    setPendingAction(null);
  };

  const pendingLabel =
    (pendingAction && PENDING_ACTION_LABELS[pendingAction]) || 'Processing…';

  const saveDraft = async () => {
    if (submitting) return null;
    if (!toLocationId) {
      setError('Select a destination location');
      return null;
    }
    if (!hasRequestReason) {
      setError('Transfer reason is required');
      return null;
    }
    beginPending('save-draft');
    try {
      if (isEditDraft && transferId) {
        const res = await axios.patch(
          API_ENDPOINTS.LOCATION_TRANSFERS.UPDATE(transferId),
          {
            to_location_id: parseInt(toLocationId, 10),
            request_reason: requestReason.trim(),
          },
          { headers }
        );
        return res.data?.transfer;
      }

      const res = await axios.post(
        API_ENDPOINTS.LOCATION_TRANSFERS.CREATE,
        {
          device_id: device.id,
          to_location_id: parseInt(toLocationId, 10),
          request_reason: requestReason.trim(),
          submit: false,
        },
        { headers }
      );
      return res.data?.transfer;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save draft');
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraftOnly = async () => {
    const saved = await saveDraft();
    if (!saved) return;
    setDraftSavedInfo(saved);
    setMessage('');
    onSuccess?.({ draftSaved: true });
  };

  const applySubmitSuccess = (transfer) => {
    setSubmittedInfo(null);
    setDraftSavedInfo(null);
    setError('');
    setMessage('');
    onSuccess?.({ submitted: true, transfer: transfer || null });
  };

  const handleSubmitForApproval = async () => {
    if (submitting) return;
    if (!toLocationId) {
      setError('Select a destination location');
      return;
    }
    if (!hasRequestReason) {
      setError('Transfer reason is required');
      return;
    }
    beginPending('submit');
    try {
      let id = transferId;
      if (isCreate) {
        const res = await axios.post(
          API_ENDPOINTS.LOCATION_TRANSFERS.CREATE,
          {
            device_id: device.id,
            to_location_id: parseInt(toLocationId, 10),
            request_reason: requestReason.trim(),
            submit: true,
          },
          { headers }
        );
        applySubmitSuccess(res.data?.transfer);
        return;
      }

      if (isEditDraft) {
        await axios.patch(
          API_ENDPOINTS.LOCATION_TRANSFERS.UPDATE(transferId),
          {
            to_location_id: parseInt(toLocationId, 10),
            request_reason: requestReason.trim(),
          },
          { headers }
        );
        id = transferId;
      }

      const res = await axios.post(API_ENDPOINTS.LOCATION_TRANSFERS.SUBMIT(id), {}, { headers });
      applySubmitSuccess(res.data?.transfer);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit transfer');
      clearPending();
    }
  };

  const postAction = async (url, body = {}, actionKey = '') => {
    if (submitting) return;
    beginPending(actionKey);
    try {
      const res = await axios.post(url, body, { headers });
      onSuccess?.({ completed: true, transfer: res.data?.transfer ?? null });
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
      clearPending();
    }
  };

  const transferDeviceName =
    device?.name || transfer?.device_name || viewTransfer?.device_name || 'this device';
  const isDraftCancel = transferStatus === 'draft';

  const handleCancelTransferClick = () => {
    if (!transferId || submitting || !canCancelAsInitiator) return;
    setCancelReason('');
    setShowCancelConfirmation(true);
  };

  const handleCancelConfirmationDismiss = () => {
    if (pendingAction === 'cancel') return;
    setShowCancelConfirmation(false);
    setCancelReason('');
  };

  const handleConfirmCancelTransfer = async () => {
    if (!transferId || submitting) return;
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setError('Please provide a reason for cancellation');
      return;
    }

    beginPending('cancel');
    try {
      const res = await axios.post(
        API_ENDPOINTS.LOCATION_TRANSFERS.CANCEL(transferId),
        { cancellation_reason: trimmedReason },
        { headers }
      );
      setShowCancelConfirmation(false);
      setCancelReason('');
      onSuccess?.({ cancelled: true, transfer: res.data?.transfer ?? null });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel transfer');
      clearPending();
    }
  };

  const renderInitiatorCancelButton = () => {
    if (!canCancelAsInitiator) return null;
    const isDraft = transferStatus === 'draft';
    const label =
      pendingAction === 'cancel'
        ? isDraft
          ? 'Discarding…'
          : 'Cancelling…'
        : isDraft
          ? 'Discard draft'
          : 'Cancel transfer';
    return (
      <button
        type="button"
        className="transfer-btn-discard"
        onClick={handleCancelTransferClick}
        disabled={submitting}
      >
        {label}
      </button>
    );
  };

  const renderSubmitSuccess = () => {
    const t = submittedInfo;
    const deviceName = device?.name || t?.device_name || 'Device';
    const deviceIdentifier = device?.device_identifier || t?.device_identifier;
    const fromName = t?.from_location_name;
    const toName = t?.to_location_name;

    return (
      <div className="transfer-submit-success-panel">
        <StatusNotice variant="success" className="transfer-submit-success-notice">
          Your transfer request has been <strong>submitted for source approval</strong>.
        </StatusNotice>
        <p className="transfer-modal-hint">
          Source administrators will review the request. The device is locked for edits until the
          transfer completes or is rejected.
        </p>
        <div className="transfer-submit-success-summary">
          <p className="transfer-modal-device-line">
            <strong>{deviceName}</strong>
            {deviceIdentifier && <DeviceIDBadge deviceId={deviceIdentifier} size="small" />}
          </p>
          {(fromName || toName) && (
            <div className="transfer-submit-success-route">
              <span className="transfer-submit-success-route-label">Route</span>
              <TransferRouteStack
                fromLocation={fromName || 'Source'}
                toLocation={toName || 'Destination'}
                fromDepartment={t?.previous_department_name}
                toDepartment={t?.destination_department_name}
              />
            </div>
          )}
          <div className="transfer-submit-success-status">
            <TransferStatusBadge status={t?.status || 'pending_source_approval'} size="medium" />
          </div>
        </div>
      </div>
    );
  };

  const renderDraftSavedSuccess = () => (
    <div className="transfer-draft-saved-panel">
      <p>
        Your transfer request is saved as a <strong>draft</strong>. The device stays active at this
        location until you submit for approval.
      </p>
      <p className="transfer-modal-hint">
        To continue: open <strong>Location Transfers → Outgoing → Drafts</strong>, select this
        transfer, then submit for approval when ready.
      </p>
      {draftSavedInfo?.to_location_name && (
        <p>
          Destination: <strong>{draftSavedInfo.to_location_name}</strong>
        </p>
      )}
    </div>
  );

  const rejectionStatusForItem = (item) =>
    item.rejection_stage === 'destination' ? 'rejected_by_destination' : 'rejected_by_source';

  const renderPriorRejectionGate = () => (
    <div className="transfer-prior-rejection-panel">
      <p className="transfer-modal-hint">
        This device had an earlier transfer request that was rejected. Review the details below
        before starting a new transfer.
      </p>
      <ul className="transfer-prior-rejection-list">
        {priorRejections.map((item) => {
          const hasRequestor =
            item.requested_by_name || item.requested_by_role || item.request_reason;
          const hasRejector = item.rejected_by_name || item.rejected_by_role || item.reason;

          return (
            <li key={item.transfer_id} className="transfer-prior-rejection-item">
              <div className="transfer-prior-rejection-top">
                {item.rejected_at && (
                  <span className="transfer-prior-rejection-date-badge" title="Rejected on">
                    {formatTransferDateTime(item.rejected_at)}
                  </span>
                )}
                <TransferStatusBadge status={rejectionStatusForItem(item)} size="small" />
              </div>

              <div className="transfer-prior-rejection-route">
                <TransferRouteStack
                  compact
                  fromLocation={item.from_location_name}
                  toLocation={item.to_location_name}
                  fromDepartment={item.previous_department_name}
                  toDepartment={item.destination_department_name}
                />
              </div>

              <section className="transfer-prior-rejection-section transfer-prior-rejection-section--requestor">
                <h4 className="transfer-prior-rejection-section-title">Requestor</h4>
                {(item.requested_by_name || item.requested_by_role) && (
                  <div className="transfer-prior-rejection-actor">
                    <TransferActor name={item.requested_by_name} role={item.requested_by_role} />
                    {item.requested_at && (
                      <span className="transfer-prior-rejection-submeta">
                        Requested {formatTransferDateTime(item.requested_at)}
                      </span>
                    )}
                  </div>
                )}
                {item.request_reason ? (
                  <p className="transfer-prior-rejection-detail">
                    <span className="transfer-prior-rejection-detail-label">Transfer reason</span>
                    {item.request_reason}
                  </p>
                ) : (
                  !hasRequestor && (
                    <p className="transfer-prior-rejection-detail transfer-prior-rejection-detail--muted">
                      No request details recorded.
                    </p>
                  )
                )}
              </section>

              <section className="transfer-prior-rejection-section transfer-prior-rejection-section--rejector">
                <h4 className="transfer-prior-rejection-section-title">Rejector</h4>
                {(item.rejected_by_name || item.rejected_by_role) && (
                  <div className="transfer-prior-rejection-actor">
                    <TransferActor name={item.rejected_by_name} role={item.rejected_by_role} />
                  </div>
                )}
                {item.reason ? (
                  <p className="transfer-prior-rejection-detail">
                    <span className="transfer-prior-rejection-detail-label">Rejection reason</span>
                    {item.reason}
                  </p>
                ) : (
                  !hasRejector && (
                    <p className="transfer-prior-rejection-detail transfer-prior-rejection-detail--muted">
                      No rejection details recorded.
                    </p>
                  )
                )}
              </section>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderDecisionHistory = (t) => {
    if (!t) return null;
    const blocks = [];
    const { status } = t;

    if (t.reviewed_at_source || t.source_decision_reason) {
      const rejected = status === 'rejected_by_source';
      blocks.push(
        <div
          key="source"
          className={`transfer-decision-block ${rejected ? 'transfer-decision-block--reject' : 'transfer-decision-block--approve'}`}
        >
          <h4>{rejected ? 'Rejected at source' : 'Approved at source'}</h4>
          {(t.reviewed_by_source_name || t.reviewed_by_source_role) && (
            <p className="transfer-decision-detail transfer-decision-actor">
              <span className="transfer-decision-label">By</span>
              <span className="transfer-decision-value">
                <TransferActor name={t.reviewed_by_source_name} role={t.reviewed_by_source_role} />
              </span>
            </p>
          )}
          {t.reviewed_at_source && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">When</span>
              <span className="transfer-decision-value">
                {formatTransferDateTime(t.reviewed_at_source)}
              </span>
            </p>
          )}
          {t.source_decision_reason && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">{rejected ? 'Reason' : 'Comment'}</span>
              <span className="transfer-decision-value">{t.source_decision_reason}</span>
            </p>
          )}
        </div>
      );
    }

    if (t.reviewed_at_destination || t.destination_decision_reason) {
      const rejected = status === 'rejected_by_destination';
      blocks.push(
        <div
          key="destination"
          className={`transfer-decision-block ${rejected ? 'transfer-decision-block--reject' : 'transfer-decision-block--approve'}`}
        >
          <h4>{rejected ? 'Rejected at destination' : 'Accepted at destination'}</h4>
          {(t.reviewed_by_destination_name || t.reviewed_by_destination_role) && (
            <p className="transfer-decision-detail transfer-decision-actor">
              <span className="transfer-decision-label">By</span>
              <span className="transfer-decision-value">
                <TransferActor
                  name={t.reviewed_by_destination_name}
                  role={t.reviewed_by_destination_role}
                />
              </span>
            </p>
          )}
          {t.reviewed_at_destination && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">When</span>
              <span className="transfer-decision-value">
                {formatTransferDateTime(t.reviewed_at_destination)}
              </span>
            </p>
          )}
          {t.destination_decision_reason && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">{rejected ? 'Reason' : 'Comment'}</span>
              <span className="transfer-decision-value">{t.destination_decision_reason}</span>
            </p>
          )}
        </div>
      );
    }

    if (status === 'cancelled' && (t.cancellation_reason || t.updated_at)) {
      blocks.push(
        <div key="cancelled" className="transfer-decision-block transfer-decision-block--reject">
          <h4>{t.requested_at ? 'Cancelled by initiator' : 'Draft discarded'}</h4>
          {(t.requested_by_name || t.requested_by_role) && (
            <p className="transfer-decision-detail transfer-decision-actor">
              <span className="transfer-decision-label">By</span>
              <span className="transfer-decision-value">
                <TransferActor name={t.requested_by_name} role={t.requested_by_role} />
              </span>
            </p>
          )}
          {t.updated_at && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">When</span>
              <span className="transfer-decision-value">
                {formatTransferDateTime(t.updated_at)}
              </span>
            </p>
          )}
          {t.cancellation_reason && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">Reason</span>
              <span className="transfer-decision-value">{t.cancellation_reason}</span>
            </p>
          )}
        </div>
      );
    }

    if (blocks.length === 0) return null;

    return (
      <div className="transfer-decision-history">
        <h4 className="transfer-decision-history-title">Decision history</h4>
        {blocks}
      </div>
    );
  };

  const renderDraftForm = () => (
    <>
      <p className="transfer-modal-device-line">
        <strong>{device?.name || transfer?.device_name || 'Device'}</strong>
        {(device?.device_identifier || transfer?.device_identifier) && (
          <>
            {' '}
            <DeviceIDBadge
              deviceId={device?.device_identifier || transfer?.device_identifier}
              size="small"
            />
          </>
        )}
      </p>
      <p className="transfer-modal-hint">
        Save a draft to finish later, or submit for approval to start the transfer workflow (device
        will lock after submit).
        {canCancelAsInitiator && ' You can discard the draft at any time before submitting.'}
      </p>
      <label>
        Destination location
        <select value={toLocationId} onChange={(e) => setToLocationId(e.target.value)}>
          <option value="">Select location</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {[loc.name, loc.city, loc.state].filter(Boolean).join(' — ')}
            </option>
          ))}
        </select>
        {locations.length === 0 && !error && (
          <p className="transfer-modal-hint">No other active locations are available in this organization.</p>
        )}
      </label>
      <label>
        Transfer reason *
        <textarea
          value={requestReason}
          onChange={(e) => setRequestReason(e.target.value)}
          rows={3}
          required
          placeholder="Why is this device being transferred?"
        />
      </label>
    </>
  );

  const renderView = () => {
    const t = viewTransfer || transfer;
    if (!t) {
      return <p className="transfer-modal-hint">Transfer details are not available.</p>;
    }
    return (
    <>
      <div className="transfer-modal-device-header">
        <h3>{t.device_name || 'Device'}</h3>
        <DeviceIDBadge deviceId={t.device_identifier || 'N/A'} size="small" />
      </div>
      <TransferViewDetails transfer={t} variant="location" />
      {renderDecisionHistory(t)}
      {mode === 'approve-source' && isAdmin && (
        <>
          <div className="transfer-action-section">
            <label>
              Comment (optional)
              <textarea value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} rows={2} />
            </label>
            <label>
              Rejection reason (required to reject)
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={2} />
            </label>
          </div>
        </>
      )}
      {mode === 'accept-destination' && isAdmin && (
        <>
          <div className="transfer-device-view-action">
            <button
              type="button"
              className="action-btn action-secondary"
              onClick={() => setShowDeviceView(true)}
            >
              View device (read-only)
            </button>
            <p className="transfer-modal-hint">
              Review full device details before accepting or rejecting this transfer.
            </p>
          </div>
          <label>
            Destination department *
            <select value={departmentId} onChange={handleDestinationDepartmentChange}>
              <option value="">Select department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            {t.previous_department_name && (
              <p className="transfer-modal-hint">
                At source: <strong>{t.previous_department_name}</strong>
              </p>
            )}
          </label>
          <label>
            Destination physical location *
            <input
              type="text"
              value={destinationPhysicalLocation}
              onChange={(e) => setDestinationPhysicalLocation(e.target.value)}
              placeholder="e.g. Ward 3B — Room 12"
              maxLength={500}
            />
            {t.previous_physical_location && (
              <p className="transfer-modal-hint">
                At source: <strong>{t.previous_physical_location}</strong>
              </p>
            )}
          </label>
          <label>
            Destination custodian *
            <select
              value={custodianId}
              onChange={(e) => setCustodianId(e.target.value)}
              disabled={!departmentId || custodiansLoading || custodians.length === 0}
            >
              <option value="">
                {!departmentId
                  ? 'Select department first'
                  : custodiansLoading
                    ? 'Loading custodians...'
                    : custodians.length === 0
                      ? 'No custodians for this department'
                      : 'Select custodian'}
              </option>
              {custodians.map((cust) => (
                <option key={cust.id} value={cust.employee_id}>
                  {cust.full_name}
                  {cust.employee_id ? ` (${cust.employee_id})` : ''}
                </option>
              ))}
            </select>
            {departmentId && !custodiansLoading && custodians.length === 0 && (
              <p className="transfer-modal-hint transfer-modal-hint--warning">
                No active custodians are assigned to this department at your location.
              </p>
            )}
          </label>
          <label>
            Primary technician *
            <select
              value={primaryTechId}
              onChange={(e) => setPrimaryTechId(e.target.value)}
              disabled={!departmentId || techniciansLoading || technicians.length === 0}
            >
              <option value="">
                {!departmentId
                  ? 'Select department first'
                  : techniciansLoading
                    ? 'Loading technicians...'
                    : technicians.length === 0
                      ? 'No technicians for this department'
                      : 'Select primary technician'}
              </option>
              {technicians.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.full_name}
                  {tech.employee_id ? ` (${tech.employee_id})` : ''}
                </option>
              ))}
            </select>
            {departmentId && !techniciansLoading && technicians.length === 0 && (
              <p className="transfer-modal-hint transfer-modal-hint--warning">
                No active technicians are assigned to this department at the destination location.
              </p>
            )}
          </label>
          <label>
            Secondary technician (optional)
            <select
              value={secondaryTechId}
              onChange={(e) => setSecondaryTechId(e.target.value)}
              disabled={!departmentId || techniciansLoading || technicians.length === 0}
            >
              <option value="">None</option>
              {technicians
                .filter((tech) => String(tech.id) !== String(primaryTechId))
                .map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.full_name}
                    {tech.employee_id ? ` (${tech.employee_id})` : ''}
                  </option>
                ))}
            </select>
          </label>
          <div className="transfer-action-section">
            <label>
              Comment (optional)
              <textarea value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} rows={2} />
            </label>
            <label>
              Rejection reason (required to reject)
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={2} />
            </label>
          </div>
        </>
      )}
    </>
    );
  };

  const getModalTitle = () => {
    if (submittedInfo) return '✅ Submitted for approval';
    if (draftSavedInfo) return '📝 Draft saved';
    if (showPriorRejectionGate) return '⚠️ Previous transfer rejection';
    if (isEditDraft) return '📝 Continue location transfer draft';
    if (isCreate) return '🚚 Transfer Location';
    if (mode === 'approve-source') return '📋 Review location transfer (source)';
    if (mode === 'accept-destination') return '📋 Accept location transfer (destination)';
    return '🚚 Location transfer details';
  };

  const renderModalFooter = () => {
    if (submittedInfo) {
      return (
        <TransferModalFooter>
          <button type="button" className="action-btn action-secondary" onClick={onClose}>
            Close
          </button>
          {onNavigateToLocationTransfers && (
            <button
              type="button"
              className="action-btn action-primary"
              onClick={() => {
                onNavigateToLocationTransfers({ direction: 'outgoing', statusTab: 'action_required' });
                onClose();
              }}
            >
              View transfers
            </button>
          )}
        </TransferModalFooter>
      );
    }

    if (draftSavedInfo) {
      return (
        <TransferModalFooter
          start={renderInitiatorCancelButton()}
          end={
            <>
              <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={submitting}>
                Close
              </button>
              {onNavigateToLocationTransfers && (
                <button
                  type="button"
                  className="action-btn action-secondary"
                  onClick={() => {
                    onNavigateToLocationTransfers({ direction: 'outgoing', statusTab: 'drafts' });
                    onClose();
                  }}
                >
                  Go to Drafts
                </button>
              )}
              <button
                type="button"
                className="action-btn action-primary"
                onClick={() => {
                  setDraftSavedInfo(null);
                  onSuccess?.({ stayOpen: true });
                }}
              >
                Continue editing
              </button>
            </>
          }
        />
      );
    }

    if (showPriorRejectionGate) {
      return (
        <TransferModalFooter>
          <button type="button" className="action-btn action-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="action-btn action-primary"
            onClick={() => setPriorAcknowledged(true)}
          >
            Continue anyway
          </button>
        </TransferModalFooter>
      );
    }

    if (isDraftForm) {
      return (
        <TransferModalFooter
          start={renderInitiatorCancelButton()}
          end={
            <>
              <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={submitting}>
                {isEditDraft ? 'Close' : 'Cancel'}
              </button>
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={handleSaveDraftOnly}
                disabled={submitting || !toLocationId || !hasRequestReason}
              >
                {pendingAction === 'save-draft' ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                className="action-btn action-approve"
                onClick={handleSubmitForApproval}
                disabled={submitting || !toLocationId || !hasRequestReason}
              >
                {pendingAction === 'submit' ? 'Submitting…' : '✅ Submit for approval'}
              </button>
            </>
          }
        />
      );
    }

    if (mode === 'approve-source' && isAdmin) {
      return (
        <TransferModalFooter>
          <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            type="button"
            className="action-btn action-reject"
            disabled={submitting || !rejectionReason.trim()}
            onClick={() =>
              postAction(API_ENDPOINTS.LOCATION_TRANSFERS.REJECT_SOURCE(transferId), {
                rejection_reason: rejectionReason,
              })
            }
          >
            {submitting ? 'Processing...' : '⚠️ Reject'}
          </button>
          <button
            type="button"
            className="action-btn action-approve"
            disabled={submitting}
            onClick={() =>
              postAction(
                API_ENDPOINTS.LOCATION_TRANSFERS.APPROVE_SOURCE(transferId),
                { decision_reason: decisionReason },
                'approve-source'
              )
            }
          >
            {pendingAction === 'approve-source' ? 'Approving…' : '✅ Approve at source'}
          </button>
        </TransferModalFooter>
      );
    }

    if (mode === 'accept-destination' && isAdmin) {
      return (
        <TransferModalFooter>
          <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            type="button"
            className="action-btn action-reject"
            disabled={submitting || !rejectionReason.trim()}
            onClick={() =>
              postAction(
                API_ENDPOINTS.LOCATION_TRANSFERS.REJECT_DESTINATION(transferId),
                { rejection_reason: rejectionReason },
                'reject-destination'
              )
            }
          >
            {pendingAction === 'reject-destination' ? 'Rejecting…' : '⚠️ Reject'}
          </button>
          <button
            type="button"
            className="action-btn action-approve"
            disabled={
              submitting ||
              !departmentId ||
              !destinationPhysicalLocation.trim() ||
              !custodianId ||
              custodiansLoading ||
              !primaryTechId ||
              techniciansLoading
            }
            onClick={() =>
              postAction(
                API_ENDPOINTS.LOCATION_TRANSFERS.ACCEPT_DESTINATION(transferId),
                {
                  destination_custodian_employee_id: custodianId.trim(),
                  destination_department_id: parseInt(departmentId, 10),
                  destination_physical_location: destinationPhysicalLocation.trim(),
                  destination_primary_technician_id: parseInt(primaryTechId, 10),
                  destination_secondary_technician_id: secondaryTechId
                    ? parseInt(secondaryTechId, 10)
                    : null,
                  decision_reason: decisionReason,
                },
                'accept-destination'
              )
            }
          >
            {pendingAction === 'accept-destination' ? 'Accepting…' : '✅ Accept at destination'}
          </button>
        </TransferModalFooter>
      );
    }

    if (isView && canCancelAsInitiator) {
      return (
        <TransferModalFooter
          start={renderInitiatorCancelButton()}
          end={
            <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={submitting}>
              Close
            </button>
          }
        />
      );
    }

    return (
      <TransferModalFooter>
        <button type="button" className="action-btn action-secondary" onClick={onClose}>
          Close
        </button>
      </TransferModalFooter>
    );
  };

  let bodyContent;
  if (submittedInfo) {
    bodyContent = renderSubmitSuccess();
  } else if (draftSavedInfo) {
    bodyContent = renderDraftSavedSuccess();
  } else if (showPriorRejectionGate) {
    bodyContent = renderPriorRejectionGate();
  } else if (isDraftForm) {
    bodyContent = renderDraftForm();
  } else {
    bodyContent = renderView();
  }

  const deviceIdForPreview = viewTransfer?.device_id || transfer?.device_id;

  return (
    <>
      {ReactDOM.createPortal(
        <div
          className="transfer-modal-overlay"
          onClick={closeOnBackdropClick ? onClose : undefined}
          role="presentation"
        >
          <div
            className={`transfer-modal-content${submitting ? ' transfer-modal-content--busy' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-modal-title"
            aria-busy={submitting}
          >
            {submitting && <TransferModalBusyOverlay label={pendingLabel} />}
            <div className="transfer-modal-header">
              <h2 id="transfer-modal-title">{getModalTitle()}</h2>
              <ModalCloseButton onClick={onClose} title="Close" disabled={submitting} />
            </div>

            <div className="transfer-modal-body">
              {error && <div className="transfer-modal-error">{error}</div>}
              {message && <div className="transfer-modal-success">{message}</div>}
              {bodyContent}
            </div>

            {renderModalFooter()}
          </div>
        </div>,
        document.body
      )}

      {showDeviceView && transferId && deviceIdForPreview && (
        <TransferDeviceViewModal
          transferId={transferId}
          deviceId={deviceIdForPreview}
          token={token}
          user={user}
          onClose={() => setShowDeviceView(false)}
        />
      )}

      <ConfirmationModal
        isOpen={showCancelConfirmation}
        entityName={transferDeviceName}
        entityType={isDraftCancel ? 'transfer draft' : 'transfer request'}
        action={isDraftCancel ? 'discard' : 'cancel'}
        actionVerb={
          isDraftCancel
            ? 'discard the transfer draft for'
            : 'cancel the transfer request for'
        }
        customMessage={
          isDraftCancel
            ? 'The device stays at the current location. You can start a new transfer later.'
            : 'Source approval will stop and the device will be unlocked for edits.'
        }
        confirmDisabled={!cancelReason.trim()}
        onConfirm={handleConfirmCancelTransfer}
        onCancel={handleCancelConfirmationDismiss}
        isLoading={pendingAction === 'cancel'}
      >
        <label className="transfer-cancel-reason-field">
          {isDraftCancel ? 'Reason for discarding draft' : 'Reason for cancellation'} *
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
            required
            placeholder={
              isDraftCancel
                ? 'Why are you discarding this draft?'
                : 'Why are you cancelling this transfer request?'
            }
            className="transfer-cancel-reason-textarea"
          />
        </label>
      </ConfirmationModal>
    </>
  );
};

export default LocationTransferModal;
